import { createAsyncThunk } from "@reduxjs/toolkit";
import { groupsApi } from "../../api/groupsApi";
import { GroupItem, GroupRequest } from "../../types/groupItem";
import { AppDispatch, RootState } from "../store";
import { setEditMode } from "../reducers/tableManagementReducer";
import { PaginationParameters } from "../../api/Pagination";
import { handleRequest } from "../utils/handleRequest";
import { PaginationResult } from "../../api/Pagination";
import { GroupFilterParameters, GroupsPageFiltersResponse } from "../../types/groupPageFilters";
import i18n from "../../i18n";

export const fetchGroupsThunk = createAsyncThunk<
  GroupItem[],
  { companyId?: number } | void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "groups/fetchGroups",
  async (args, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<GroupItem[]>(
      dispatch,
      "indirect",
      () => typeof args === "object" && args.companyId
        ? groupsApi.getAllGroupsOfCompany(args.companyId)
        : groupsApi.getAllGroups()
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to fetch groups");
    }
    return res.result as GroupItem[];
  }
);

export const fetchAdminGroupsThunk = createAsyncThunk<
  GroupItem[],
  void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "groups/fetchAdminGroups",
  async (_, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<GroupItem[]>(
      dispatch,
      "indirect",
      () => groupsApi.getAllGroups()
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to fetch groups");
    }
    return res.result as GroupItem[];
  }
);

export const fetchGroupPageFiltersThunk = createAsyncThunk<
  GroupsPageFiltersResponse | null,
  void,
  { dispatch: AppDispatch; state: RootState }
>("groups/fetchGroupPageFilters", async (_, { dispatch }) => {
  const callResult = await handleRequest(dispatch, "indirect", () => groupsApi.getGroupPageFilters());
  return callResult.success() && callResult.result ? callResult.result : null;
});

export const fetchPaginatedGroupsThunk = createAsyncThunk<
  PaginationResult<GroupItem>,
  { paginationParams: PaginationParameters; filters: GroupFilterParameters },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "groups/fetchPaginatedGroups",
  async ({ paginationParams, filters }, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<PaginationResult<GroupItem>>(
      dispatch,
      "indirect",
      () => groupsApi.getGroups(paginationParams, filters)
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to fetch paginated groups");
    }
    return res.result as PaginationResult<GroupItem>;
  }
);

export const createGroupThunk = createAsyncThunk<
  GroupItem,
  GroupRequest,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "groups/createGroup",
  async (groupData, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<GroupItem>(
      dispatch,
      "direct",
      () => groupsApi.createGroup(groupData),
      i18n.t("requestMessage.groupCreated")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to create group");
    }
    await dispatch(fetchGroupsThunk());
    dispatch(setEditMode(false));
    return res.result as GroupItem;
  }
);

export const editGroupThunk = createAsyncThunk<
  void,
  { group: GroupRequest, id: number },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "groups/editGroup",
  async ({ group, id }, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<GroupItem>(
      dispatch,
      "direct",
      () => groupsApi.editGroup(id, group),
      i18n.t("requestMessage.groupUpdated")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to update group");
    }
    await dispatch(fetchGroupsThunk());
    dispatch(setEditMode(false));
  }
);

export const deleteGroupThunk = createAsyncThunk<
  number,
  number,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "groups/deleteGroup",
  async (id, { dispatch, rejectWithValue }) => {
    const res = await handleRequest<void>(
      dispatch,
      "direct",
      () => groupsApi.deleteGroup(id),
      i18n.t("requestMessage.groupDeleted")
    );
    if (!res.success()) {
      return rejectWithValue(res.errorMessage ?? "Failed to delete group");
    }
    await dispatch(fetchGroupsThunk());
    dispatch(setEditMode(false));
    return id;
  }
);